/**
 * Store de período seleccionado.
 * - monthIds: lista de IDs de meses (vacío = sin selección)
 * - range: modo de selección (single | lastN | all)
 * - lastN: cantidad de meses hacia atrás (1, 2, 3, ..., 12, all)
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type PeriodRange = 'single' | 'lastN' | 'all';

interface PeriodState {
  monthIds: string[];
  range: PeriodRange;
  lastN: number; // 1..12, o 999 para "all"
  allMonthIds: string[]; // cache de todos los disponibles (ordenados por año/mes desc)
  setAllMonthIds: (ids: string[]) => void;
  selectMonth: (id: string) => void;
  selectLastN: (n: number) => void;
  selectAll: () => void;
  selectNone: () => void;
}

export const usePeriodStore = create<PeriodState>()(
  persist(
    (set, get) => ({
      monthIds: [],
      range: 'single',
      lastN: 1,
      allMonthIds: [],

      setAllMonthIds: (ids) => {
        set({ allMonthIds: ids });
        // Si no hay selección y hay meses, auto-seleccionar el más reciente
        const current = get();
        if (current.monthIds.length === 0 && ids.length > 0) {
          // Asumimos que vienen en orden descendente (más reciente primero)
          set({ monthIds: [ids[0]], range: 'single', lastN: 1 });
        } else {
          // Filtrar los monthIds actuales para que solo contengan IDs válidos
          const validCurrent = current.monthIds.filter((id) => ids.includes(id));
          if (validCurrent.length !== current.monthIds.length) {
            set({ monthIds: validCurrent });
          }
        }
      },

      selectMonth: (id) => {
        set({ monthIds: [id], range: 'single', lastN: 1 });
      },

      selectLastN: (n) => {
        const ids = get().allMonthIds;
        const sliced = ids.slice(0, n);
        set({ monthIds: sliced, range: 'lastN', lastN: n });
      },

      selectAll: () => {
        const ids = get().allMonthIds;
        set({ monthIds: ids, range: 'all', lastN: 999 });
      },

      selectNone: () => {
        set({ monthIds: [], range: 'single', lastN: 0 });
      },
    }),
    { name: 'chef-analist-period' },
  ),
);

/**
 * Helper para construir el query string de monthIds.
 */
export function monthIdsQuery(monthIds: string[]): string {
  return monthIds.join(',');
}
